import React, { useState } from 'react';
import { Paperclip } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import '../../component-styles/KanbanBoard.css';
import AssigneeAvatars from './AssigneeAvatars';

const COLUMNS = [
  { id: 'todo', label: 'To Do' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'done', label: 'Done' },
];

function KanbanCard({ todo, team, projectNameMap, projectScoped, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: todo.todoId,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="kanban-card"
      {...listeners}
      {...attributes}
      onClick={() => onEdit(todo)}
    >
      <div className="kanban-card-title">{todo.title}</div>

      <AssigneeAvatars assignees={todo.assignees} team={team} />

      <div className="kanban-card-meta">
        <span className={`priority-badge priority-${todo.priority || 'normal'}`}>
          {todo.priority || 'normal'}
        </span>
        {todo.attachments?.length > 0 && (
          <span
            className="todo-tag todo-tag-attachments"
            title={todo.attachments.map((a) => a.name).join(', ')}
          >
            <Paperclip size={11} />
            {todo.attachments.length}
          </span>
        )}
        {todo.dueDate && <span className="muted">Due {todo.dueDate}</span>}
      </div>

      {!projectScoped && (
        <div className="kanban-card-project">
          {todo.projectId
            ? projectNameMap[String(todo.projectId)] || 'Project'
            : 'Internal / Non-Project'}
        </div>
      )}
    </div>
  );
}

function KanbanColumn({ column, todos, team, projectNameMap, projectScoped, onEdit }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <div className={`kanban-column ${isOver ? 'drag-over' : ''}`} ref={setNodeRef}>
      <div className="kanban-column-head">
        <span className="kanban-column-title">{column.label}</span>
        <span className="kanban-column-count">{todos.length}</span>
      </div>

      <div className="kanban-column-body">
        {todos.length === 0 ? (
          <div className="kanban-empty muted">No tasks</div>
        ) : (
          todos.map((t) => (
            <KanbanCard
              key={t.todoId}
              todo={t}
              team={team}
              projectNameMap={projectNameMap}
              projectScoped={projectScoped}
              onEdit={onEdit}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function KanbanBoard({
  todos,
  team,
  projectNameMap,
  projectScoped,
  onStatusChange,
  onEdit,
}) {
  const [activeTodo, setActiveTodo] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const byColumn = COLUMNS.reduce((acc, col) => {
    acc[col.id] = todos.filter((t) => (t.status || 'todo') === col.id);
    return acc;
  }, {});

  function handleDragStart(event) {
    const todo = todos.find((t) => t.todoId === event.active.id);
    setActiveTodo(todo || null);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    setActiveTodo(null);
    if (!over) return;

    const todo = todos.find((t) => t.todoId === active.id);
    if (!todo) return;

    const newStatus = over.id;
    if (newStatus === (todo.status || 'todo')) return;

    onStatusChange(todo.todoId, newStatus);
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveTodo(null)}
    >
      <div className="kanban-board">
        {COLUMNS.map((col) => (
          <KanbanColumn
            key={col.id}
            column={col}
            todos={byColumn[col.id]}
            team={team}
            projectNameMap={projectNameMap}
            projectScoped={projectScoped}
            onEdit={onEdit}
          />
        ))}
      </div>

      <DragOverlay>
        {activeTodo ? (
          <div className="kanban-card kanban-card-overlay">
            <div className="kanban-card-title">{activeTodo.title}</div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
